import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { MENTION_COLOR_MAP } from '@/app/constants';
import { useFetch, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { safeStorage } from '@/app/utils/client';
import { materialsBatchGetElements, materialsBatchHistoryElements } from '../apis';
import type { ViduV1Element } from '../types';

interface UseCheckMaterialElementsProps {
  referencePrompts: ReferencePrompt[];
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>, shouldTriggerCallback?: boolean) => void;
  updateTextPrompt: (updater: (prev: TextPromptWithInputItem) => TextPromptWithInputItem) => void;
}

const LOCAL_STORAGE_DO_NOT_REMIND_DIALOGS_KEY = 'VIDU_DO_NOT_REMIND_DIALOGS';

export const useCheckMaterialElements = ({
  referencePrompts,
  updateReferenceAndMentions,
  updateTextPrompt,
}: UseCheckMaterialElementsProps) => {
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));

  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');

  const forbiddenHandler = (forbiddenPrompts: ReferencePrompt[]) => {
    const ids: string[] = [];
    forbiddenPrompts.forEach((prompt) => {
      updateReferenceAndMentions(
        prompt.id,
        {
          status: 'forbidden',
          prompt: null,
        },
        false,
      );
      ids.push(prompt.id);
    });

    // mention 里没有 ID 的主体，颜色设置为 forbidden
    if (forbiddenPrompts.length > 0) {
      updateTextPrompt((prev) =>
        prev.map((item) => {
          if (item.type === 'mention' && (!item.mention.id || ids.includes(item.mention.id))) {
            return { ...item, mention: { ...item.mention, color: MENTION_COLOR_MAP.forbidden } };
          }
          return item;
        }),
      );
    }
  };

  const { run: getElements } = useFetch(
    (
      list: {
        id: string;
        version: string | undefined;
      }[],
    ) =>
      Promise.all([
        materialsBatchHistoryElements({
          body: {
            ids: list,
          },
        }),
        materialsBatchGetElements({
          query: {
            ids: list?.map((it) => it.id),
          },
        }),
      ]),
    {
      manual: true,
      onSuccess: (results, params) => {
        const allMyElementsMap = results[1].data?.elements || {};
        const myElementList: ViduV1Element[] = [];
        const historyElements = results[0].data?.elements?.filter((it) => {
          let historyTag = true;
          const element = allMyElementsMap[it.id || ''];
          if (element) {
            // 自己的主体，永远使用最新版，不使用历史版本
            if (element.creator_id === userInfo?.id) {
              historyTag = false;
            }
          }
          if (!historyTag) {
            myElementList.push(element);
          }
          return historyTag;
        });

        const forbiddenList: string[] = [];
        historyElements?.forEach((reference) => {
          // 更新内容
          if (reference.id) {
            if (reference.creator_id === userInfo?.id) {
              // 自己的主体永远使用最新版本。历史版本里已经过滤掉 myElementList 里的数据了，所以这里如果有值，代表自己已经删掉该主体了，需要 forbidden
              forbiddenList.push(reference.id);
              return;
            }
            updateReferenceAndMentions(
              reference.id,
              {
                prompt: reference,
                name: reference.name,
                id: reference.id,
                status: 'done',
                type: 'element',
              },
              false,
            );
          }
        });
        const recreatingPrompts = referencePrompts.filter(
          (prompt) => prompt.type === 'element' && prompt.status === 'recreating',
        );

        myElementList.forEach((reference) => {
          if (reference.id) {
            const referencePrompt = referencePrompts.find((prompt) => prompt.id === reference?.id);

            if (!referencePrompt) return;

            // 更新内容
            updateReferenceAndMentions(
              reference.id,
              {
                prompt: reference,
                name: reference.name,
                id: reference.id,
                status: 'done',
                type: 'element',
              },
              false,
            );

            // 提示版本已修改
            if (
              referencePrompt?.prompt &&
              'version' in referencePrompt.prompt &&
              reference.version !== referencePrompt.prompt.version
            ) {
              // 检查是否已选择不再提示
              const doNotRemindTypes = JSON.parse(safeStorage.getItem(LOCAL_STORAGE_DO_NOT_REMIND_DIALOGS_KEY) || '{}');
              if (doNotRemindTypes.elementModified) {
                return;
              }

              openConfirmDialog({
                content: t('create.char2VideoForm.dialog.elementModified.content'),
                secondaryButtonText: t('create.char2VideoForm.dialog.elementModified.closeButtonText'),
                primaryButtonText: t('create.char2VideoForm.dialog.confirmButtonText'),
                onSecondaryAction: () => {
                  // 处理"不再提示"逻辑
                  const doNotRemindTypes = JSON.parse(
                    safeStorage.getItem(LOCAL_STORAGE_DO_NOT_REMIND_DIALOGS_KEY) || '{}',
                  );
                  doNotRemindTypes.elementModified = true;
                  safeStorage.setItem(LOCAL_STORAGE_DO_NOT_REMIND_DIALOGS_KEY, JSON.stringify(doNotRemindTypes));
                },
              });
            }
          }
        });
        // 如果 historyElements 和 myElementList 都没返回，说明这期间有数据删除了/取消投稿导致接口拿不到，需要 forbidden
        forbiddenList.push(
          ...params[0]
            .filter(
              (it) => !historyElements?.find((his) => it.id === his.id) && !myElementList.find((my) => it.id === my.id),
            )
            .map((it) => it.id),
        );
        forbiddenHandler(recreatingPrompts.filter((it) => forbiddenList.includes(it.id)));
      },
    },
  );

  // 做同款时批量查询主体信息（是否已删除、是否已修改、并且获取主体的详细信息）
  useEffect(() => {
    const checkMaterialElements = () => {
      const recreatingPrompts = referencePrompts.filter(
        (prompt) => prompt.type === 'element' && prompt.status === 'recreating',
      );

      forbiddenHandler(recreatingPrompts.filter((prompt) => !prompt.id));
      const referenceList = recreatingPrompts
        .filter(({ id }) => id)
        .map((it) => ({
          id: it.id,
          version: it.type === 'element' ? it.prompt?.version : undefined,
        }));
      if (referenceList.length > 0) {
        getElements(referenceList);
      }
    };

    if (referencePrompts.length > 0) {
      checkMaterialElements();
    }
  }, [referencePrompts]);
};
